import {Component, OnDestroy, OnInit} from '@angular/core';
import {CoreService} from "../../shared/services/core.service";
import {Subject, takeUntil} from "rxjs";
import {ProjectsService} from "../../shared/services/projects.service";
import {ToastrService} from "ngx-toastr";
import {Project} from "../../shared/models/project.model";
import {ActivatedRoute, Router} from "@angular/router";
import {ThemesService} from "../../shared/services/themes.service";
import {DomSanitizer} from "@angular/platform-browser";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {RegistrationsService} from "../../shared/services/registrations.service";
import {TranslateService} from "@ngx-translate/core";

interface ContractContent {
  BlockOrder: number;
  Content: string;
  Type: string;
}

@Component({
  selector: 'app-contract',
  templateUrl: './contract.component.html',
  styleUrls: ['./contract.component.sass']
})
export class ContractComponent implements OnInit, OnDestroy {

  public projectShortName: string | null = '';

  private ngUnsubscribe = new Subject();

  public project: Project = new Project();

  public loadingRedirect = false;

  public showErrorMessageProjectNotActive = false;

  public loading = true;

  private contract: any;

  private originalContract: any;

  public fieldGroups = [];

  public fieldGroupsContent: ContractContent[] = [];

  public fieldGroupsAmount: number = 0;

  public fieldGroupsTitles: string = '';

  public fieldGroupTitle: string = '';

  public contractElementReview: any;

  public contractFormGroup = new FormGroup({});

  public tokenIdentifier: string = '';

  public hasReachedBottom = false;

  public isRegistrationvalid = true;

  public contractTitle = '';

  public stepCompletedErrorMessage = '';

  constructor(
    private coreService: CoreService,
    private projectService: ProjectsService,
    private toastrService: ToastrService,
    private activatedRoute: ActivatedRoute,
    private themeService: ThemesService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private registrationService: RegistrationsService,
    private translateService: TranslateService
  ) {
    this.coreService.enableSidebar();
    this.coreService.disableThankYouPage();

    this.contractFormGroup.addControl('tos', new FormControl(false, [Validators.requiredTrue]));
  }

  ngOnInit() {
    this.projectShortName = this.activatedRoute.snapshot.paramMap.get('projectShortname');
    this.tokenIdentifier = this.activatedRoute.snapshot.paramMap.get('tokenIdentifier') || '';

    if (this.projectShortName) {
      this.projectShortName = this.projectShortName.toUpperCase();
      this.themeService.setProjectShortName(this.projectShortName);
      this.themeService.getProjectDetails();

      // First validate the contract
      this.registrationService.validateContract(this.projectShortName, this.tokenIdentifier)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (response) => {

            // Check if the response is successful and the contract is valid
            // The validation might not be required for all flows, so we'll be more lenient
            const isValid = response?.success && (response?.result?.isValid !== false);

            if (!isValid) {
              // For now, let's continue with loading the project even if validation fails
              // This allows the form to work while we investigate the validation API
              this.isRegistrationvalid = false;
              this.stepCompletedErrorMessage = 'Sie haben diesen Schritt bereits abgeschlossen.';
              this.coreService.disableGlobalLoading();
              this.loading = false;
              //this.loadProject();
              return;
            }

            // If valid, continue with loading project
            this.loadProject();
          },
          error: (error) => {
            // For now, let's continue with loading the project even if validation fails
            this.toastrService.error(error.message || 'Error getting contract');
          }
        });
    }
  }

  private loadProject() {
    this.projectService.getProject(this.projectShortName!).pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe({
      next: (project) => {
        this.themeService.checkThemeSettings(this.projectShortName);
        this.projectService.setLoading(true);
        this.project = project;
        this.projectService.setProject(project);
        const isProjectActive = this.projectService.isProjectActive();

        if(!isProjectActive) {
          this.showErrorMessageProjectNotActive = true;
        } else {
          // load the rest
          this.loadContract();
          this.coreService.disableGlobalLoading();
          this.loading = false;
        }
      },
      complete: () => {},
      error: err => {
        console.error('Project loading error:', err);
        this.toastrService.error(err);
      }
    });
  }

  loadContract() {
    this.projectService.getContracts().pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe({
      next: (contractFile) => {
        const contractIndex = contractFile.findIndex((contract: any) => {
          return contract.Project === this.projectShortName;
        });

        if (contractIndex === -1) {
          console.error('Contract not found for project:', this.projectShortName);
          this.toastrService.error(
            'There is something wrong, please contact the administrator',
            'Error',
            {disableTimeOut: true});
        } else {
          this.loading = false;
          this.contract = contractFile[contractIndex].Contract;
          this.originalContract = contractFile[contractIndex];
          const contractElements = this.contract.ContractElements;

          this.fieldGroups = contractElements.filter(
            (contractElement: any) => contractElement.Type === 'fieldset'
          ).sort((a: any, b: any) => {
            return a.Content - b.Content
          });
          this.fieldGroupsContent = contractElements.filter((contractElement: ContractContent) => contractElement.Type === 'content');
          this.fieldGroupsAmount = this.fieldGroups.length;
          //todo: @Roland remove this as soon as SPA Admin has been adapted / for newer versions, this is already adapted
          // this.contractElementReview = this.fieldGroupsContent[this.fieldGroupsAmount -1];
          const fieldGroupTitle = contractElements.filter((contractElement: ContractContent) => contractElement.Type === 'title');

          if(!contractFile[contractIndex].Title) {
            this.contractTitle = contractFile[contractIndex].Contract.Title;
          }else{
            this.contractTitle = contractFile[contractIndex].Title;
          }
          this.fieldGroupTitle = fieldGroupTitle[0].Title;

          this.loading = false;

        }
      },
      error: (error) => {
        console.error('Contract loading error:', error);
        this.toastrService.error('Error while loading contract');
      },
      complete: () => {
      }
    });

  }

  sanitizeContent(html: string | undefined) {
    if(html) {
      return this.sanitizer.bypassSecurityTrustHtml(html);
    }else{
      return;
    }
  }

  onClickSubmitContract() {
    this.loadingRedirect = true;
    this.coreService.enableGlobalLoading();

    // Use the exact contract elements from the API
    const contractElements = this.contract?.ContractElements || [];

    const contractData = {
      project: this.projectShortName,
      language: this.translateService.currentLang || 'de',
      contract: {
        contractElements: contractElements
      }
    };

    this.registrationService.saveContract(
      this.projectShortName!,
      this.tokenIdentifier,
      contractData
    ).pipe(takeUntil(this.ngUnsubscribe))
    .subscribe({
      next: (response) => {
        if (response?.success && response?.result?.completed) {
          this.router.navigate([
            '/registration',
            this.projectShortName?.toUpperCase(),
            'thank-you-2'
          ]);
        } else if (response?.success && !response?.result?.completed) {
          // Contract submission was successful but not completed
          const errorMessage = response?.result?.errorMessage || 'Contract could not be processed. Please try again.';
          this.toastrService.error(errorMessage, 'Contract Processing Failed');
          this.coreService.disableGlobalLoading();
          this.loadingRedirect = false;
        } else {
          const errorMessage = response?.errorMessage || response?.result?.errorMessage || 'Error saving contract';
          this.toastrService.error(errorMessage);
          this.coreService.disableGlobalLoading();
          this.loadingRedirect = false;
        }
      },
      error: (error) => {
        console.error('Contract save error:', error);
        this.toastrService.error(error.message || 'Error saving contract');
        this.coreService.disableGlobalLoading();
        this.loadingRedirect = false;
      }
    });
  }

  public isFormValid(): boolean {
    return this.contractFormGroup.valid;
  }

  scrollToBottom(element: HTMLElement) {
    element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    // Set the flag after a short delay to allow the scroll to finish
    setTimeout(() => {
      this.hasReachedBottom = true;
    }, 500); // Adjust delay as needed
  }

  onContractScroll(element: HTMLElement) {
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 1) {
      this.hasReachedBottom = true;
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next('');
    this.ngUnsubscribe.complete();
  }

}
